/**
 * Error handling utilities for VFW2 prechecks
 * Provides helper functions to work with standardized error codes
 */

import {
  BackgroundNoiseCheckErrorCode,
  BandwidthCheckErrorCode,
  ERROR_CATALOG,
  MicrophoneCheckErrorCode,
  SpeakerCheckErrorCode,
  StandardizedError,
  SystemCheckErrorCode,
} from '../types/errorCatalog';

/**
 * System Check specific error details for different failure scenarios
 */
export interface SystemCheckErrorDetails {
  checkId: string;
  checkName: string;
  standardizedCode: string;
  error: StandardizedError;
}

/**
 * Maps System Check failure IDs to standardized error codes
 */
export function getSystemCheckError(
  checkId: string
): SystemCheckErrorDetails | null {
  let standardizedCode: string;
  let checkName: string;

  switch (checkId) {
    case 'BROWSER_VERSION':
      standardizedCode = SystemCheckErrorCode.BROWSER_VERSION_UNSUPPORTED;
      checkName = 'Browser Version Check';
      break;
    case 'BROWSER_COMPATIBILITY':
      standardizedCode = SystemCheckErrorCode.BROWSER_NOT_SUPPORTED;
      checkName = 'Browser Compatibility Check';
      break;
    case 'NETWORK':
      standardizedCode = SystemCheckErrorCode.NETWORK_TEST_FAILED;
      checkName = 'Network Check';
      break;
    default:
      standardizedCode = SystemCheckErrorCode.SYSTEM_CHECK_FAILED;
      checkName = 'System Check';
  }

  const error = ERROR_CATALOG[standardizedCode];
  if (!error) {
    return null;
  }

  return {
    checkId,
    checkName,
    standardizedCode,
    error,
  };
}

/**
 * Get standardized error for microphone check failures
 */
export function getMicrophoneCheckError(
  failureType?:
    | 'NOT_DETECTED'
    | 'ACCESS_DENIED'
    | 'LEVEL_LOW'
    | 'TEST_FAILED'
    | 'NO_VOICE_DETECTED'
): StandardizedError {
  let code: string;

  switch (failureType) {
    case 'NOT_DETECTED':
      code = MicrophoneCheckErrorCode.MICROPHONE_NOT_DETECTED;
      break;
    case 'ACCESS_DENIED':
      code = MicrophoneCheckErrorCode.MICROPHONE_ACCESS_DENIED;
      break;
    case 'LEVEL_LOW':
      code = MicrophoneCheckErrorCode.MICROPHONE_LEVEL_TOO_LOW;
      break;
    case 'TEST_FAILED':
      code = MicrophoneCheckErrorCode.MICROPHONE_TEST_FAILED;
      break;
    case 'NO_VOICE_DETECTED':
      code = MicrophoneCheckErrorCode.MICROPHONE_NO_VOICE_DETECTED;
      break;
    default:
      code = MicrophoneCheckErrorCode.MICROPHONE_CHECK_FAILED;
  }

  return ERROR_CATALOG[code];
}

export type MicrophoneCheckFailureType = Parameters<
  typeof getMicrophoneCheckError
>[0];

/** Maps CADE failure `details` strings to microphone standardized error input. */
export function cadeMicrophoneDetailsToFailureType(
  details: string | null
): MicrophoneCheckFailureType {
  switch (details) {
    case 'ERROR_MICROPHONE':
      return 'NOT_DETECTED';
    case 'ERROR':
      return 'NO_VOICE_DETECTED';
    case 'ERROR_WE_CANT_HEAR_YOU':
    case 'ERROR_MICROPHONE_WE_CANT_HEAR_YOU':
      return 'LEVEL_LOW';
    default:
      return undefined;
  }
}

/**
 * Get standardized error for speaker check failures
 */
export function getSpeakerCheckError(
  failureType?: 'NOT_DETECTED' | 'AUDIO_NOT_HEARD'
): StandardizedError {
  let code: string;

  switch (failureType) {
    case 'NOT_DETECTED':
      code = SpeakerCheckErrorCode.SPEAKER_NOT_DETECTED;
      break;
    case 'AUDIO_NOT_HEARD':
      code = SpeakerCheckErrorCode.SPEAKER_AUDIO_NOT_HEARD;
      break;
    default:
      code = SpeakerCheckErrorCode.SPEAKER_CHECK_FAILED;
  }

  return ERROR_CATALOG[code];
}

export type SpeakerCheckFailureType = Parameters<
  typeof getSpeakerCheckError
>[0];

/** Maps CADE failure `details` strings to speaker standardized error input. */
export function cadeSpeakerDetailsToFailureType(
  details: string | null
): SpeakerCheckFailureType {
  switch (details) {
    case 'AUDIO_NOT_HEARD':
      return 'AUDIO_NOT_HEARD';
    default:
      return undefined;
  }
}

/**
 * Get standardized error for bandwidth check failures
 */
export function getBandwidthCheckError(
  failureType?: 'UPLOAD_LOW' | 'DOWNLOAD_LOW' | 'UNSTABLE'
): StandardizedError {
  let code: string;

  switch (failureType) {
    case 'DOWNLOAD_LOW':
      code = BandwidthCheckErrorCode.DOWNLOAD_SPEED_TOO_LOW;
      break;
    case 'UNSTABLE':
      code = BandwidthCheckErrorCode.NETWORK_UNSTABLE;
      break;
    default:
      code = BandwidthCheckErrorCode.BANDWIDTH_CHECK_FAILED;
  }

  return ERROR_CATALOG[code];
}

/**
 * Get standardized error for background noise check failures
 */
export function getBackgroundNoiseCheckError(): StandardizedError {
  return ERROR_CATALOG[BackgroundNoiseCheckErrorCode.NOISE_CHECK_FAILED];
}
